import * as Sentry from "@sentry/nextjs"

import useToast from "@hooks/useToast"
import { useRebookMutation } from "@mono/essentials/entities/booking/hooks"
import { useUpdateQuantityOfOrderItemMutation } from "@mono/essentials/entities/order/hooks"
import { RebookRequestType } from "@mono/essentials/types/BookingInfoTypes"
import { GetOrderDetailsResponseType } from "@mono/essentials/types/OrderTypes"

import { TDynamicRentalDetailsFormAttributes } from "./handleRebookWithAnotherProduct"

export const useHandleUpdateDynamicRentalBookingDetails = () => {
  const { showToast } = useToast()
  const { mutateAsync: updateQuantityOfOrderItemMutation } = useUpdateQuantityOfOrderItemMutation()
  const { mutateAsync: rebookMutation } = useRebookMutation()

  const handleUpdateDynamicRentalBookingDetails = async ({
    productInfo,
    orderItemId,
    onSuccess,
    onError,
    orderData,
  }: {
    productInfo: TDynamicRentalDetailsFormAttributes
    orderItemId: string
    orderData: GetOrderDetailsResponseType
    onSuccess?: () => Promise<void>
    onError?: (error: unknown) => Promise<void>
  }) => {
    try {
      if (productInfo && orderData?.id && orderItemId) {
        const selectedOrderItem = orderData.items.find((oi) => oi.id === orderItemId)

        // Reschedule
        const reschedulePayload: RebookRequestType = {}
        if (productInfo?.available_option && !productInfo?.is_single_day) {
          reschedulePayload.availability_id = productInfo?.product_availability_id
          reschedulePayload.end_at = productInfo?.units[0]?.end_at
          reschedulePayload.start_at = productInfo?.units[0]?.start_at
          reschedulePayload.unit_id = productInfo?.duration_type
        } else if (productInfo?.is_single_day) {
          reschedulePayload.slot_id = productInfo?.slot_id
          reschedulePayload.unit_id = productInfo?.duration_type
        } else if (!productInfo?.available_option) {
          reschedulePayload.slot_id = productInfo.time
        }
        if (selectedOrderItem?.booking?.id)
          await rebookMutation({
            bookingId: selectedOrderItem.booking.id,
            body: reschedulePayload,
            filter: {
              is_overusing_resources_checked: productInfo?.is_overusing_resources_checked ?? false,
            },
          })

        // Update the quantity
        await updateQuantityOfOrderItemMutation({
          orderId: orderData.id,
          orderItemId,
          payload: {
            quantities: productInfo.units.map((unit: any) => ({
              unit_id: unit.unitId ?? "",
              quantity: unit.unitCount ?? 1,
              booking_id: selectedOrderItem?.booking?.id,
            })),
          },
        })

        await onSuccess?.()
      } else {
        showToast({
          heading: "Unable to update the booking! please try again later.",
          type: "danger",
        })
      }
    } catch (error) {
      Sentry.captureException(error)
      await onError?.(error)
    }
  }

  return { handleUpdateDynamicRentalBookingDetails }
}
